import jsPDF from 'jspdf';
import 'jspdf-autotable';
import React, { useState, useContext, useEffect } from "react";
import { QuotationContext } from './context/QuotationContext';
import { InvoiceContext } from './context/InvoiceContext';
import { useUserContext } from './context/UserContext';
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Fab,
  Modal,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GetAppIcon from "@mui/icons-material/GetApp";
import DeleteIcon from "@mui/icons-material/Delete";
import TransformIcon from "@mui/icons-material/Transform";

import { useNavigate } from "react-router-dom";
import { getQuotationsList, deleteQuot } from "../DatabaseApi/DatabaseApi";
import { setDoc, doc } from 'firebase/firestore';
import { db } from '../firebase.config';
const isAdmin = true;

// ## FIXED: Consolidated and cleaned up styles for better consistency ##
const commonCellContentStyle = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  maxWidth: '160px', // Set a max-width for the content itself
  display: 'block'
};

const tableCellStyles = {
  qId: { width: '12%' },
  quotationTo: { width: '16%', maxWidth: '180px' },
  date: { width: '12%' },
  shipTo: { width: '16%', maxWidth: '180px' },
  amount: { width: '12%' },
  validUntil: { width: '12%' },
  actions: { width: '20%', minWidth: '150px' }
};


export default function QuotationList() {
  const { deleteQuotation } = useContext(QuotationContext);
  const { addInvoice, setSelectedItems } = useContext(InvoiceContext) || {};
  const { quotationValidityDays } = useUserContext();
  const navigate = useNavigate();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quotList, setQuotList] = useState([]);
  const [converting, setConverting] = useState(false);
  const [allowEditing, setAllowEditing] = useState(true);
  
  const [invoiceCount, setInvoiceCount] = useState(() => {
    const stored = localStorage.getItem('invoiceCount');
    return stored ? JSON.parse(stored) : {};
  });

  useEffect(() => {
    async function getQList() {
      try {
        const tempquotarray = await getQuotationsList(1);
        const quotationsArray = Array.isArray(tempquotarray) ? tempquotarray : [tempquotarray];
        const qList = quotationsArray.map(q => {
          try {
            const quotation = typeof q === 'string' ? JSON.parse(q) : q;
            // ## ADDED: Ensures all necessary fields have default values to prevent errors ##
            return {
              qId: quotation.qId,
              shipTo: quotation.shipTo || 'N/A',
              quotationTo: quotation.quotationTo || 'N/A',
              date: quotation.date,
              validity: quotation.validity,
              items: quotation.items || [],
              additionalNotes: quotation.additionalNotes || '',
              termsAndConditions: quotation.termsAndConditions || '',
              cgstAmount: quotation.cgstAmount || 0,
              sgstAmount: quotation.sgstAmount || 0,
              igstAmount: quotation.igstAmount || 0,
              taxableAmount: quotation.taxableAmount || 0,
              totalAmount: quotation.totalAmount || 0,
              totalGstAmount: quotation.totalGstAmount || 0,
              customFooter: quotation.customFooter || '',
              coveringLetter: quotation.coveringLetter || ''
            };
          } catch (error) {
            console.error('Error parsing quotation:', error);
            return null;
          }
        }).filter(q => q !== null);

        setQuotList(qList);
      } catch (error) {
        console.error('Error fetching quotations:', error);
        setQuotList([]);
      }
    }
    getQList();
  }, []);

  const handleNavigate = () => navigate("/quotation-templates/qtemplate4");
  
  const handleView = (quotation) => {
    setSelectedQuote(quotation);
    setViewModalOpen(true);
  };

  const handleConvertToInvoice = (quotation) => {
    setSelectedQuote(quotation);
    setConvertModalOpen(true);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-GB'); // Use a consistent format like DD/MM/YYYY
    } catch (e) {
      return dateString;
    }
  };

  const calculateDueDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    date.setDate(date.getDate() + 7);
    return date.toISOString().split('T')[0];
  };

  const generateInvoiceId = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dateKey = date.toISOString().split('T')[0];
    let newCount = (invoiceCount[dateKey] || 0) + 1;

    setInvoiceCount(prev => {
      const updated = { ...prev, [dateKey]: newCount };
      localStorage.setItem('invoiceCount', JSON.stringify(updated));
      return updated;
    });

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const fullYear = date.getFullYear().toString();
    const serialNumber = newCount.toString().padStart(3, '0');
    return `IN${day}${month}${fullYear}${serialNumber}`;
  };

  // ## FIXED: Corrected the totalAmount calculation to account for discounts ##
  const transformQuotationItemsToInvoiceFormat = (quotationItems) => {
    return (quotationItems || []).map((item, index) => {
        const quantity = item.moq || item.quantity || 0;
        const price = item.unitPrice || item.price || 0;
        const discount = item.unitDiscount || item.discount || 0;
        const lineTotal = (quantity * price) * (1 - discount / 100);

        return {
            sr: index + 1,
            description: item.productDescription || item.description || '',
            hsn: item.hsnCode || item.hsn || '',
            gst: item.gst || item.unitTaxRate || 0,
            quantity: quantity,
            price: price,
            discount: discount,
            totalAmount: lineTotal
        };
    });
  };

  const confirmConvertToInvoice = async () => {
    if (!selectedQuote) return;
    setConverting(true);
    try {
      const currentDate = new Date().toISOString().split('T')[0];
      const invoiceId = generateInvoiceId(currentDate);
      const transformedItems = transformQuotationItemsToInvoiceFormat(selectedQuote.items || []);

      const invoiceData = {
        iId: invoiceId, date: currentDate, invoiceTo: selectedQuote.quotationTo,
        shipTo: selectedQuote.shipTo, items: transformedItems, // ## UPDATED: Use transformed items ##
        taxableAmount: selectedQuote.taxableAmount || 0, cgstAmount: selectedQuote.cgstAmount || 0,
        sgstAmount: selectedQuote.sgstAmount || 0, igstAmount: selectedQuote.igstAmount || 0,
        totalGstAmount: selectedQuote.totalGstAmount || 0, totalAmount: selectedQuote.totalAmount || 0,
        additionalNotes: selectedQuote.additionalNotes || '', termsAndConditions: selectedQuote.termsAndConditions || '',
        lastUpdatedOn: new Date().toISOString(), lastUpdatedBy: selectedQuote.quotationTo,
        dueDate: calculateDueDate(currentDate), status: "active", customFooter: selectedQuote.customFooter || '',
        authorizedSignature: null, authorizedSignatureName: '', customerSignature: null, customerSignatureName: '',
        originalQuotationId: selectedQuote.qId, convertedFromQuotation: true,
        conversionDate: new Date().toISOString()
      };

      if (allowEditing) {
        if (setSelectedItems) {
          setSelectedItems(transformedItems);
        }
        localStorage.setItem('prefilledInvoiceData', JSON.stringify({
          ...invoiceData // Pass all data
        }));
        setConvertModalOpen(false);
        setConverting(false);
        navigate('/invoice-templates/template4', { 
          state: { isFromQuotation: true, quotationData: selectedQuote, prefilledData: invoiceData }
        });
      } else {
        await setDoc(doc(db, "invoices", invoiceId), invoiceData);
        if (addInvoice) {
          addInvoice(invoiceData);
        }
        setConvertModalOpen(false);
        setConverting(false);
        alert(`Quotation ${selectedQuote.qId} has been successfully converted to Invoice ${invoiceId}`);
        navigate('/InvoiceList');
      }
    } catch (error) {
      console.error("Error converting quotation to invoice:", error);
      alert("Error converting quotation to invoice. Please try again.");
      setConverting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₹ ${parseFloat(amount || 0).toFixed(2)}`;
  };

  const calculateValidUntil = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      const validity = parseInt(quotationValidityDays, 10) || 7;
      date.setDate(date.getDate() + validity);
      return formatDate(date.toISOString());
    } catch (e) {
      return "N/A";
    }
  };
  
  // ## FIXED: Completed the PDF generation to include all quotation data ##
  const generatePDF = (quote) => {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.width;
    const leftMargin = 15;
    const rightMargin = 15;
    const contentWidth = pageWidth - leftMargin - rightMargin;
    let currentY = 20;

    // --- Header ---
    pdf.setFontSize(20).setFont('helvetica', 'bold').text('QUOTATION', leftMargin, currentY);
    currentY += 15;

    // --- Quotation Info & Client Info ---
    pdf.setLineWidth(0.5).line(leftMargin, currentY, pageWidth - rightMargin, currentY);
    currentY += 10;

    const leftColY = currentY;
    pdf.setFontSize(10).setFont('helvetica', 'bold').text(`Quotation ID:`, leftMargin, leftColY);
    pdf.setFont('helvetica', 'normal').text(`${quote.qId || 'N/A'}`, leftMargin + 35, leftColY);
    pdf.setFont('helvetica', 'bold').text(`Quotation Date:`, leftMargin, leftColY + 7);
    pdf.setFont('helvetica', 'normal').text(`${formatDate(quote.date) || 'N/A'}`, leftMargin + 35, leftColY + 7);
    pdf.setFont('helvetica', 'bold').text(`Valid Until:`, leftMargin, leftColY + 14);
    pdf.setFont('helvetica', 'normal').text(`${quote.validity || calculateValidUntil(quote.date) || 'N/A'}`, leftMargin + 35, leftColY + 14);

    const rightColX = pageWidth / 2 + 10;
    let rightColY = currentY;
    pdf.setFont('helvetica', 'bold').text(`Bill To:`, rightColX, rightColY);
    const clientLines = pdf.splitTextToSize(quote.quotationTo || 'N/A', contentWidth / 2 - 15);
    pdf.setFont('helvetica', 'normal').text(clientLines, rightColX, rightColY + 5);
    rightColY += clientLines.length * 5 + 5;
    
    pdf.setFont('helvetica', 'bold').text(`Ship To:`, rightColX, rightColY);
    const shipToLines = pdf.splitTextToSize(quote.shipTo || 'N/A', contentWidth / 2 - 15);
    pdf.setFont('helvetica', 'normal').text(shipToLines, rightColX, rightColY + 5);
    
    currentY = Math.max(leftColY + 25, rightColY + shipToLines.length * 5 + 10);
    
    // --- Items Table ---
    const tableHeaders = [['Product', 'HSN', 'Qty (MOQ)', 'Price', 'Discount', 'GST', 'Amount']];
    const tableData = (quote.items || []).map(item => {
      const unitPrice = parseFloat(item.unitPrice || 0);
      const quantity = parseFloat(item.moq || 0);
      const discount = parseFloat(item.unitDiscount || 0);
      const amount = (quantity * unitPrice) * (1 - discount/100);
      return [
        item.productDescription || 'N/A', item.hsnCode || 'N/A', quantity,
        unitPrice.toFixed(2), `${discount.toFixed(2)}%`, `${item.gst || 0}%`, amount.toFixed(2)
      ];
    });

    pdf.autoTable({
      startY: currentY, head: tableHeaders, body: tableData, theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: leftMargin, right: rightMargin },
    });
    currentY = pdf.lastAutoTable.finalY + 10;
    
    // --- Totals Section ---
    const totalsX = pageWidth / 2 + 30;
    pdf.setFontSize(10).setFont('helvetica', 'bold');
    pdf.text('Taxable Amount:', totalsX, currentY);
    pdf.setFont('helvetica', 'normal').text(formatCurrency(quote.taxableAmount), pageWidth - rightMargin, currentY, { align: 'right' });
    currentY += 7;
    if (quote.cgstAmount > 0) {
      pdf.setFont('helvetica', 'bold').text('CGST:', totalsX, currentY);
      pdf.setFont('helvetica', 'normal').text(formatCurrency(quote.cgstAmount), pageWidth - rightMargin, currentY, { align: 'right' });
      currentY += 7;
    }
    if (quote.sgstAmount > 0) {
      pdf.setFont('helvetica', 'bold').text('SGST:', totalsX, currentY);
      pdf.setFont('helvetica', 'normal').text(formatCurrency(quote.sgstAmount), pageWidth - rightMargin, currentY, { align: 'right' });
      currentY += 7;
    }
    if (quote.igstAmount > 0) {
      pdf.setFont('helvetica', 'bold').text('IGST:', totalsX, currentY);
      pdf.setFont('helvetica', 'normal').text(formatCurrency(quote.igstAmount), pageWidth - rightMargin, currentY, { align: 'right' });
      currentY += 7;
    }
    pdf.setLineWidth(0.2).line(totalsX, currentY - 3, pageWidth - rightMargin, currentY - 3);
    pdf.setFontSize(12).setFont('helvetica', 'bold');
    pdf.text('Grand Total:', totalsX, currentY + 2);
    pdf.text(formatCurrency(quote.totalAmount), pageWidth - rightMargin, currentY + 2, { align: 'right' });
    currentY += 15;

    // --- Notes and Terms ---
    if (quote.additionalNotes) {
      pdf.setFontSize(10).setFont('helvetica', 'bold').text('Additional Notes:', leftMargin, currentY);
      currentY += 5;
      pdf.setFont('helvetica', 'normal').text(pdf.splitTextToSize(quote.additionalNotes, contentWidth), leftMargin, currentY);
      currentY += pdf.getTextDimensions(pdf.splitTextToSize(quote.additionalNotes, contentWidth)).h + 10;
    }
    if (quote.termsAndConditions) {
      pdf.setFontSize(10).setFont('helvetica', 'bold').text('Terms and Conditions:', leftMargin, currentY);
      currentY += 5;
      pdf.setFont('helvetica', 'normal').text(pdf.splitTextToSize(quote.termsAndConditions, contentWidth), leftMargin, currentY);
    }

    // --- Footer ---
    if (quote.customFooter) {
        const pageCount = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            pdf.setPage(i);
            pdf.setFontSize(8).setFont('helvetica', 'italic').text(quote.customFooter, pageWidth / 2, pdf.internal.pageSize.height - 10, { align: 'center' });
        }
    }

    return pdf;
  };


  const handleDownload = (quote) => {
    try {
      const pdf = generatePDF(quote);
      const safeQId = (quote.qId || 'unknown').replace(/[^\w\d-]/g, '_');
      const safeClientName = (quote.quotationTo || 'client').replace(/[^\w\d-]/g, '_');
      pdf.save(`Quotation_${safeQId}_${safeClientName}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("There was an error generating the PDF. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this quotation?")) {
      try {
        await deleteQuot(1, id);
        // ## UPDATED: Use context function if available, otherwise filter state ##
        if (deleteQuotation) {
          deleteQuotation(id);
        }
        setQuotList(prevQuotList => prevQuotList.filter(quote => quote.qId !== id));
        
      } catch (error) {
        console.error("Error deleting quotation:", error);
        alert("An error occurred while deleting. Please try again.");
      }
    }
  };

  return (
    <div>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h5" sx={{ mb: 3 }}>
            Quotation List
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={tableCellStyles.qId}>Quotation ID</TableCell>
                  <TableCell sx={tableCellStyles.quotationTo}>Bill To</TableCell>
                  <TableCell sx={tableCellStyles.date}>Date</TableCell>
                  <TableCell sx={tableCellStyles.shipTo}>Ship To</TableCell>
                  <TableCell sx={tableCellStyles.amount}>Total Amount</TableCell>
                  <TableCell sx={tableCellStyles.validUntil}>Valid Until</TableCell>
                  <TableCell sx={tableCellStyles.actions} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      No quotations available. Click the + button to create a new one.
                    </TableCell>
                  </TableRow>
                ) : (
                  quotList.map((quote) => (
                    <TableRow key={quote.qId} hover>
                      <TableCell>{quote.qId}</TableCell>
                      <TableCell>
                        <Tooltip title={quote.quotationTo}>
                          <span style={commonCellContentStyle}>{quote.quotationTo}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatDate(quote.date)}</TableCell>
                      <TableCell>
                        <Tooltip title={quote.shipTo}>
                           <span style={commonCellContentStyle}>{quote.shipTo}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatCurrency(quote.totalAmount)}</TableCell>
                      <TableCell>{calculateValidUntil(quote.date)}</TableCell>
                      <TableCell align="center">
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="View Details">
                            <IconButton size="small" color="primary" onClick={() => handleView(quote)}><VisibilityIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Download PDF">
                            <IconButton size="small" color="secondary" onClick={() => handleDownload(quote)}><GetAppIcon /></IconButton>
                          </Tooltip>
                          <Tooltip title="Convert to Invoice">
                            <IconButton size="small" color="success" onClick={() => handleConvertToInvoice(quote)}><TransformIcon /></IconButton>
                          </Tooltip>
                          {isAdmin && (
                            <Tooltip title="Delete Quotation">
                              <IconButton size="small" color="error" onClick={() => handleDelete(quote.qId)}><DeleteIcon /></IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Container>

      <Fab color="primary" aria-label="add" sx={{ position: "fixed", bottom: "20px", right: "20px" }} onClick={handleNavigate}>
        <AddIcon />
      </Fab>

      {/* Convert to Invoice Confirmation Modal */}
      <Dialog open={convertModalOpen} onClose={() => !converting && setConvertModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>Convert Quotation to Invoice</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ textAlign: 'center', mb: 2 }}>
            Are you sure you want to convert this quotation to an invoice?
          </DialogContentText>
          {selectedQuote && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
              <Typography><strong>Quotation ID:</strong> {selectedQuote.qId}</Typography>
              <Typography><strong>Customer:</strong> {selectedQuote.quotationTo}</Typography>
              <Typography><strong>Amount:</strong> {formatCurrency(selectedQuote.totalAmount)}</Typography>
            </Box>
          )}
          <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
            <FormControlLabel
              control={<Checkbox checked={allowEditing} onChange={(e) => setAllowEditing(e.target.checked)} color="primary" />}
              label={
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Allow editing before saving</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {allowEditing ? "Opens the invoice in an editor to modify details before final creation." : "Creates and saves the invoice directly without further editing."}
                  </Typography>
                </Box>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, pt: 0 }}>
          <Button onClick={() => setConvertModalOpen(false)} variant="outlined" color="secondary" disabled={converting}>
            Cancel
          </Button>
          <Button onClick={confirmConvertToInvoice} variant="contained" color="primary" disabled={converting}>
            {converting ? 'Converting...' : (allowEditing ? 'Convert & Edit' : 'Confirm & Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Quotation Modal */}
      <Modal open={viewModalOpen} onClose={() => setViewModalOpen(false)}>
        <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: '80%', maxWidth: 900, maxHeight: "90vh", overflowY: "auto", bgcolor: "background.paper", boxShadow: 24, p: 4, borderRadius: 2 }}>
          {selectedQuote && (
            <>
              <Typography variant="h4" component="h2" gutterBottom>Quotation: {selectedQuote.qId}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography><strong>Bill To:</strong> {selectedQuote.quotationTo}</Typography>
                  <Typography><strong>Ship To:</strong> {selectedQuote.shipTo}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography><strong>Date:</strong> {formatDate(selectedQuote.date)}</Typography>
                  <Typography><strong>Valid Until:</strong> {calculateValidUntil(selectedQuote.date)}</Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Items</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">HSN</TableCell>
                      <TableCell align="right">GST</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedQuote.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell component="th" scope="row">{item.productDescription}</TableCell>
                        <TableCell align="right">{item.moq}</TableCell>
                        <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell align="right">{item.unitDiscount}%</TableCell> {/* ## FIXED: Added % sign ## */}
                        <TableCell align="right">{item.hsnCode}</TableCell>
                        <TableCell align="right">{item.gst}%</TableCell>
                        <TableCell align="right">{formatCurrency((item.moq * item.unitPrice) * (1 - item.unitDiscount / 100))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* ## ADDED: Financial Summary in View Modal ## */}
              <Grid container sx={{ mt: 2 }}>
                <Grid item xs={6}></Grid>
                <Grid item xs={6}>
                    <Paper elevation={0} sx={{p: 2}}>
                        <Grid container>
                            <Grid item xs={6}><Typography>Taxable Amount:</Typography></Grid>
                            <Grid item xs={6}><Typography align="right">{formatCurrency(selectedQuote.taxableAmount)}</Typography></Grid>
                            
                            <Grid item xs={6}><Typography>Total GST:</Typography></Grid>
                            <Grid item xs={6}><Typography align="right">{formatCurrency(selectedQuote.totalGstAmount)}</Typography></Grid>
                            
                            <Grid item xs={12}><Divider sx={{my:1}} /></Grid>

                            <Grid item xs={6}><Typography variant="h6">Grand Total:</Typography></Grid>
                            <Grid item xs={6}><Typography variant="h6" align="right">{formatCurrency(selectedQuote.totalAmount)}</Typography></Grid>
                        </Grid>
                    </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />
              <Typography><strong>Notes:</strong> {selectedQuote.additionalNotes || 'None'}</Typography>
              <Typography><strong>Terms:</strong> {selectedQuote.termsAndConditions || 'None'}</Typography>

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button variant="outlined" onClick={() => setViewModalOpen(false)}>Close</Button>
                <Button variant="contained" color="secondary" onClick={() => handleDownload(selectedQuote)} startIcon={<GetAppIcon />}>Download</Button>
                <Button variant="contained" color="primary" onClick={() => { setViewModalOpen(false); handleConvertToInvoice(selectedQuote); }} startIcon={<TransformIcon />}>Convert to Invoice</Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
}